// Slides in after the user picks a type — replaces the navigate() call entirely.

import { useForm, useFieldArray } from "react-hook-form";
import React, { useRef, useContext, useState, useEffect, useCallback, useMemo } from "react";
import moment from 'moment';
import { bankingService } from "../../services/bankingService";
import { accountService } from "../../services/accountService";
import { createModal } from "react-modal-promise";
import GeneralModal from "../../helpers/GeneralModal";
import LogicContext from "../../Context/LogicContext";
import GeneralFileUploadPreview from "../../components/GeneralFileUploadPreview";
import { formatCustomNumber } from "../../helpers/formatCustomNumber";
import { GetManualJournalAtom, SetManualJournalAtom, GetManualJournalEditAtom, SetManualJournalEditAtom } from "../../store/manualJounalAtom";
import ManualJTransactionTypeSelect from "../manual_journals/ManualJTransactionTypeSelect";
import ManualJournalType from "../manual_journals/ManualJournalTypeSelect";


// ─── Icons ────────────────────────────────────────────────────────────────────
const PrintIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
    </svg>
);
const ChevronDown = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);
const ChevronLeft = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
);
const SearchIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0" />
    </svg>
);
const XIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);
const PlusIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);
const TrashIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);


const inputCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition";
const selectCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition appearance-none";


// ─── Label + Input helpers ─────────────────────────────────────────────────────
const FormField = ({ label, children, required }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
        {children}
    </div>
);







export const CreateManualJournalPanel = ({ isOpen, onClose, onBack, selectedType, bankName, accountId, onSaved }) => {
    const [saving, setSaving] = useState(false);
    const [attachmentFile, setAttachmentFile] = useState(null);
    const [fileKey, setFileKey] = useState(0);
    const [accountOptions, setAccountOptions] = useState([]);
    const [taxOptions, setTaxOptions] = useState([]);
    const [journalTypeList, setJournalTypeList] = useState([]);
    const [editable, setEditable] = useState({});
    const isMounted = useRef(false);

    const myPromiseModal = createModal(GeneralModal);
    const logicContext = useContext(LogicContext);
    const journalAtom = GetManualJournalAtom();
    const setManualJournalAtom = SetManualJournalAtom();
    const journalEditData = GetManualJournalEditAtom();
    const setJournalEditData = SetManualJournalEditAtom();

    const { register, handleSubmit, reset, control, watch, getValues, setValue, formState: { errors } } = useForm({
        defaultValues: {
            journal_date: moment().format('YYYY-MM-DD'),
            journal_entries: [{ account_id: accountId, remark: "", debit_amount: 0, credit_amount: 0, tax_id: "" }]
        }
    });



    const { fields, append, remove } = useFieldArray({ control, name: "journal_entries" });

    // Watch journal entries for debit/credit calculation
    const watchAllFields = watch();

    let credit = 0;
    let debt = 0;
    let debtDiff = 0;
    let creditDiff = 0;

    const calculateDebtCredit = (watchData) => {
        if (!watchData?.length) return;
        watchData.forEach((table) => {
            credit += Number(table.credit_amount) || 0;
            debt += Number(table.debit_amount) || 0;
        });

        if (credit > debt) {
            creditDiff = credit - debt;
        }
        if (debt > credit) {
            debtDiff = debt - credit;
        }
    };

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        console.log('check_journal_atom ', journalAtom);
    }, []);



    useEffect(() => {
        if (!journalAtom.account_info?.length) {
            loadTableList();
        }

    }, [accountId, bankName]);

    calculateDebtCredit(watchAllFields.journal_entries);

    // Load account options and tax info from atom
    useEffect(() => {
        if (journalAtom?.chart_of_account) {
            setAccountOptions(journalAtom.chart_of_account);
        }
        if (journalAtom?.tax_info) {
            setTaxOptions(journalAtom.tax_info);
        }
        if (journalAtom?.journal_type) {
            setJournalTypeList(journalAtom.journal_type);
        }
    }, [journalAtom]);

    const groupedAccountOptions = useMemo(() => {
        const groups = {};
        accountOptions.forEach(acc => {
            const key = acc.account_type_name || 'Other';
            if (!groups[key]) groups[key] = { label: key, accounts: [] };
            groups[key].accounts.push(acc);
        });
        return Object.values(groups);
    }, [accountOptions]);

    // Load data if not available
    useEffect(() => {
        console.log('check_atom ', journalAtom);
        if (!journalAtom?.account_info?.length) {
            accountService.manualJournal().then(res => {
                if (res.data && isMounted.current) {
                    // Data is loaded via atom in the original component
                }
            });
        }
    }, []);


    // Reset form and state when panel opens OR closes
    useEffect(() => {
        if (isOpen && !editable?.account_ref_no) {
            // Opening fresh form
            reset({
                journal_date: moment().format('YYYY-MM-DD'),
                journal_entries: [{ account_id: accountId, remark: "", debit_amount: 0, credit_amount: 0, tax_id: "" }],
                reference_number: '',
                notes: '',
                journal_type_id: '',
                transaction_type_id: '',
            });
            setAttachmentFile(null);
            setFileKey(prev => prev + 1);
        } else if (!isOpen) {
            // Cleaning up when panel is closed
            setEditable({});
            setJournalEditData([]);
            reset({
                journal_date: moment().format('YYYY-MM-DD'),
                journal_entries: [{ account_id: accountId, remark: "", debit_amount: 0, credit_amount: 0, tax_id: "" }],
                reference_number: '',
                notes: '',
                journal_type_id: '',
                transaction_type_id: '',
            });
            setAttachmentFile(null);
            setFileKey(prev => prev + 1);
        }
    }, [isOpen]);

    // Populate form when opening in edit mode
    useEffect(() => {
        if (isOpen && journalEditData?.length) {
            const tempEdit = journalEditData[0];
            setEditable(tempEdit);
            reset({
                journal_date: tempEdit?.encoded_date
                    ? moment(tempEdit.encoded_date).format('YYYY-MM-DD')
                    : moment().format('YYYY-MM-DD'),
                journal_entries: journalEditData,
                reference_number: tempEdit?.transaction_reference || '',
                notes: tempEdit?.notes || '',
                journal_type_id: tempEdit?.journal_type_id || '',
                transaction_type_id: tempEdit?.journal_type_id || '',
            });
        }
    }, [journalEditData, isOpen]);

    const addRow = () => {
        append({ account_id: "", remark: "", debit_amount: 0, credit_amount: 0, tax_id: "" });
    };

    const removeRow = (index) => {
        if (fields.length > 1) {
            remove(index);
        }
    };

    const onTypeSelect = (sel) => {
        setValue('journal_type_id', sel.journal_type_id);
    };

    const onTransSelect = (sel) => {
        setValue('transaction_type_id', sel.code);
    };

    const onFileSelected = (file) => {
        setAttachmentFile(file);
    };

    const onSubmit = async (data) => {
        if (debt === 0 && credit === 0) {
            logicContext?.showToast?.("Credit or Debit must have a value", 'error');
            return;
        }

        const result = await myPromiseModal({
            title: editable?.account_ref_no ? "Update Journal" : "Create New Manual Journal?",
            body: editable?.account_ref_no ? "Manual journal will be updated" : "A new Manual Journal will be created!",
            showInput: false
        });

        if (!result?.decision) return;

        setSaving(true);

        // Build XML string
        let temp = ``;
        data?.journal_entries.forEach(element => {
            temp += `<a><account_id>${element?.account_id}</account_id>
            <debit_amount>${element?.debit_amount || 0} </debit_amount>
            <credit_amount>${element?.credit_amount || 0} </credit_amount>
            <tax_id>${element?.tax_id} </tax_id>
            <remark>${element?.remark} </remark></a>`
        });

        let newFormData = {
            "journal_date": data.journal_date,
            "reference_number": data.reference_number,
            "journal_type_id": data?.journal_type_id,
            "transaction_type_id": data?.transaction_type_id,
            "notes": data.notes,
            "currency_id": '',
            "xml_string": `<root>${temp} </root>`,
        };

        try {
            const formData = new FormData();
            for (const keyData in newFormData) {
                formData.set(keyData, newFormData[keyData] ? newFormData[keyData] : 0);
            }
            formData.set("option", editable?.account_ref_no ? 2 : 4);
            if (editable?.account_ref_no) {
                formData.set("account_ref_no", editable.account_ref_no);
                if (editable?.attachment_url) {
                    formData.set("attachment_url", editable.attachment_url);
                }
            }

            if (attachmentFile) {
                formData.set('file', attachmentFile);
            }

            formData.delete('journal_entries');

            const res = await accountService.createNewJournal(formData);
            const { message_text: Message_text, message_id } = res.data[0] || res.data?.result_status?.[0];

            if (message_id === 1) {
                logicContext?.showToast?.(Message_text, "success");
                setEditable({});
                setJournalEditData([]);
                reset({
                    journal_date: moment().format('YYYY-MM-DD'),
                    journal_entries: [{ account_id: accountId, remark: "", debit_amount: 0, credit_amount: 0, tax_id: "" }],
                    reference_number: '',
                    notes: '',
                    journal_type_id: '',
                    transaction_type_id: '',
                });
                setAttachmentFile(null);
                setFileKey(prev => prev + 1);
                onSaved?.();
                onClose();
            } else {
                logicContext?.showToast?.(Message_text, "error");
            }
        } catch (err) {
            console.error('Failed to save journal', err);
            logicContext?.showToast?.("Failed to save journal", "error");
        } finally {
            setSaving(false);
        }
    };



    const loadTableList = () => {
        console.log('load_table_list ', bankName);
        accountService.manualJournal().then(res => {
            console.log('check_res ', res);
            if (res.data && isMounted.current) {
                setManualJournalAtom(res.data);
            }
        });
    }



    return (
        <div
            className={`fixed top-0 right-0 h-full w-[780px] bg-white z-50 flex flex-col
        transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
            style={{ borderLeft: '1px solid #e5e7eb', boxShadow: '-4px 0 24px rgba(0,0,0,0.10)' }}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                <div className="flex items-center gap-2">
                    <button
                        onClick={onBack}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition"
                        title="Back to type selection"
                    >
                        <ChevronLeft />
                    </button>
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest leading-none mb-0.5">
                            {editable?.account_ref_no ? 'Edit' : 'New'} Manual Journal
                        </p>
                        <h3 className="text-base font-bold text-gray-800 leading-tight">
                            {selectedType?.transaction_name || editable?.journal_type_name || 'Journal Entry'}
                        </h3>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition"
                >
                    <XIcon />
                </button>
            </div>

            {/* From bank context pill */}
            {bankName && (
                <div className="px-5 pt-4 pb-1 flex-shrink-0">
                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                        <span className="text-xs text-blue-700 font-medium">
                            Bank: <span className="font-bold">{bankName || 'Selected Account'}</span>
                        </span>
                    </div>
                </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

                {/* Date & Journal Number */}
                <div className="grid grid-cols-2 gap-4">
                    <FormField label="Date" required>
                        <input
                            type="date"
                            className={inputCls}
                            {...register('journal_date', { required: 'Date is required' })}
                        />
                        {errors.journal_date && <p className="text-xs text-red-500 mt-1">{errors.journal_date.message}</p>}
                    </FormField>

                    <FormField label="Journal #">
                        <input
                            type="text"
                            readOnly
                            placeholder="Auto generated"
                            value={editable?.account_ref_no || ''}
                            className={`${inputCls} bg-gray-50 text-gray-500`}
                        />
                    </FormField>
                </div>

                {/* Reference Number & Journal Type */}
                <div className="grid grid-cols-2 gap-4">
                    <FormField label="Reference #">
                        <input
                            type="text"
                            placeholder="e.g. REF-0042"
                            className={inputCls}
                            {...register('reference_number')}
                        />
                    </FormField>

                    <FormField label="Journal Type">
                        <ManualJournalType
                            list={journalAtom.journal_type}
                            defaultVal={editable?.journal_type_name}
                            onTypeSelect={onTypeSelect}
                        />
                    </FormField>
                </div>

                {/* Transaction Type & Currency */}
                <div className="grid grid-cols-2 gap-4">
                    <FormField label="Transaction Type">
                        <ManualJTransactionTypeSelect
                            disabled={editable?.transactionTypeId === 1}
                            defaultVal={editable?.transation_type_name}
                            onTypeSelect={onTransSelect}
                        />
                    </FormField>

                    <FormField label="Currency">
                        <select className={selectCls} disabled>
                            <option>NGN - Nigerian Naira</option>
                        </select>
                    </FormField>
                </div>

                {/* Notes */}
                <FormField label="Notes">
                    <textarea
                        rows={2}
                        maxLength={500}
                        placeholder="Optional notes…"
                        className={`${inputCls} resize-none`}
                        {...register('notes')}
                    />
                </FormField>

                {/* Journal Entries Table */}
                <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Journal Entries <span className="text-red-400 ml-0.5">*</span>
                    </label>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* Table Header */}
                        <div className="grid grid-cols-12 gap-1 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            <div className="col-span-3">Account</div>
                            <div className="col-span-2">Description</div>
                            <div className="col-span-2">Tax</div>
                            <div className="col-span-2 text-right">Debit</div>
                            <div className="col-span-2 text-right">Credit</div>
                            <div className="col-span-1"></div>
                        </div>

                        {/* Table Body */}
                        <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
                            {fields.map((field, index) => (
                                <div key={field.id} className="grid grid-cols-12 gap-1 px-3 py-2 items-center bg-white hover:bg-gray-50/50">
                                    {/* Account Select */}
                                    <div className="col-span-3">
                                        <select
                                            className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                            {...register(`journal_entries.${index}.account_id`)}
                                        >
                                            <option value="">Select</option>
                                            {groupedAccountOptions.map(group => (
                                                <optgroup key={group.label} label={group.label}>
                                                    {group.accounts.map(acc => (
                                                        <option key={acc.account_id} value={acc.account_id}>
                                                            {acc.account_name}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Description */}
                                    <div className="col-span-2">
                                        <input
                                            type="text"
                                            placeholder="Description"
                                            className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                            {...register(`journal_entries.${index}.remark`)}
                                        />
                                    </div>

                                    {/* Tax Select */}
                                    <div className="col-span-2">
                                        <select
                                            className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                            {...register(`journal_entries.${index}.tax_id`)}
                                        >
                                            <option value="">Select</option>
                                            {taxOptions.map(tax => (
                                                <option key={tax.tax_id} value={tax.tax_id}>
                                                    {tax.tax_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Debit */}
                                    <div className="col-span-2">
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 text-right focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                            {...register(`journal_entries.${index}.debit_amount`, { valueAsNumber: true })}
                                        />
                                    </div>

                                    {/* Credit */}
                                    <div className="col-span-2">
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 text-right focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                            {...register(`journal_entries.${index}.credit_amount`, { valueAsNumber: true })}
                                        />
                                    </div>

                                    {/* Remove Button */}
                                    <div className="col-span-1 flex justify-center">
                                        <button
                                            type="button"
                                            onClick={() => removeRow(index)}
                                            disabled={fields.length <= 1}
                                            className="w-6 h-6 flex items-center justify-center rounded text-red-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed transition"
                                        >
                                            <TrashIcon />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Add Row Button */}
                    <button
                        type="button"
                        onClick={addRow}
                        className="mt-2 flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 transition"
                    >
                        <PlusIcon />
                        Add New Row
                    </button>
                </div>

                {/* Totals Summary */}
                <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between text-xs font-semibold">
                    <div className="flex items-center gap-4">
                        <span className="text-red-600">Debit(s): {formatCustomNumber(debt)}</span>
                        <span className="text-green-600">Credit(s): {formatCustomNumber(credit)}</span>
                    </div>
                    {(debtDiff > 0 || creditDiff > 0) && (
                        <span className="text-red-500">
                            Difference: {formatCustomNumber(debtDiff || creditDiff)}
                        </span>
                    )}
                </div>

                {/* File Attachment */}
                <FormField label="Attach File">
                    <GeneralFileUploadPreview
                        key={fileKey}
                        onFileSelected={onFileSelected}
                    />
                    <p className="text-xs text-gray-400 mt-1">Maximum file size 2MB</p>
                </FormField>

                {/* spacer so last field isn't hidden behind sticky footer */}
                <div className="h-2" />
            </form>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex gap-3 flex-shrink-0">
                <button
                    type="button"
                    onClick={handleSubmit(onSubmit)}
                    disabled={saving}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold py-2.5 px-4 rounded-lg transition shadow-sm"
                >
                    {saving ? 'Saving…' : editable?.account_ref_no ? 'Update Journal' : 'Save & Publish'}
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 bg-white hover:bg-gray-100 text-gray-700 text-sm font-semibold py-2.5 px-4 rounded-lg border border-gray-200 transition"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};